import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Code as CodeIcon
} from '@mui/icons-material';

const isObjectLike = (value) => value !== null && typeof value === 'object';

const hasChildNodes = (value) => {
  if (!isObjectLike(value)) return false;
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return Object.keys(value).length > 0;
};

const formatLeafValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value.toString() : 'NaN';
  }

  return String(value);
};

const getNodeSummary = (value) => {
  if (Array.isArray(value)) {
    return `${value.length} element${value.length === 1 ? '0' : 'i'}`;
  }
  if (isObjectLike(value)) {
    const keys = Object.keys(value || {});
    return `${keys.length} ${keys.length === 1 ? 'campo' : 'campi'}`;
  }
  return formatLeafValue(value);
};

const createNodeId = (path) => (path.length === 0 ? 'root' : ['root', ...path].join('.'));

const cloneWithUpdatedPath = (target, path, newValue) => {
  if (path.length === 0) {
    return newValue;
  }

  const [currentKey, ...rest] = path;
  const isArrayTarget = Array.isArray(target);
  const key = isArrayTarget ? Number(currentKey) : currentKey;

  const clonedTarget = isArrayTarget ? [...target] : { ...target };
  const currentValue = clonedTarget[key];
  clonedTarget[key] = rest.length > 0
    ? cloneWithUpdatedPath(currentValue, rest, newValue)
    : newValue;

  return clonedTarget;
};

const coerceValue = (rawValue, originalValue) => {
  if (typeof originalValue === 'boolean') {
    return rawValue === 'true';
  }

  if (typeof originalValue === 'number') {
    if (rawValue === '' || rawValue === null) {
      return rawValue === '' ? '' : originalValue;
    }
    const parsed = Number(rawValue);
    return Number.isNaN(parsed) ? rawValue : parsed;
  }

  if (originalValue === null || originalValue === undefined) {
    return rawValue;
  }

  return rawValue;
};

const ensureDefaultExpansion = (value) => {
  if (!isObjectLike(value)) return new Set();

  const expanded = new Set();
  const entries = Array.isArray(value)
    ? value.map((_, index) => [String(index), value[index]])
    : Object.entries(value);

  entries.forEach(([key, child]) => {
    if (hasChildNodes(child)) {
      expanded.add(createNodeId([key]));
    }
  });

  return expanded;
};

const getArrayColumns = (arrayData) => {
  if (!Array.isArray(arrayData)) return [];
  
  const allKeys = new Set();
  arrayData.forEach(item => {
    if (item && typeof item === 'object' && !Array.isArray(item)) {
      Object.keys(item).forEach(key => allKeys.add(key));
    }
  });
  return Array.from(allKeys);
};

const createEmptyArrayItem = (arrayData) => {
  if (!Array.isArray(arrayData)) return '';
  
  const columns = getArrayColumns(arrayData);
  if (columns.length > 0) {
    return columns.reduce((acc, col) => ({ ...acc, [col]: '' }), {});
  }
  return '';
};

const StructuredDataTree = ({ 
  data, 
  editable = false, 
  onChange, 
  sx,
  showJsonToggle = true,
  onJsonError
}) => {
  const [expandedNodes, setExpandedNodes] = useState(() => ensureDefaultExpansion(data));
  const [isJsonMode, setIsJsonMode] = useState(false);
  const [jsonError, setJsonError] = useState(null);
  const [jsonValue, setJsonValue] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [isErrorAnimating, setIsErrorAnimating] = useState(false);
  
  // Use ref for timer to avoid dependency issues and memory leaks
  const validationTimerRef = useRef(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (validationTimerRef.current) {
        clearTimeout(validationTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setExpandedNodes((prev) => {
      const defaults = ensureDefaultExpansion(data);
      const merged = new Set([...defaults, ...prev]);
      return merged;
    });
  }, [data]);

  // Initialize JSON value when data changes
  useEffect(() => {
    if (!isJsonMode && !isDirty) {
      try {
        setJsonValue(JSON.stringify(data, null, 2));
        setIsDirty(false);
        setJsonError(null);
      } catch (error) {
        console.error('Error formatting JSON:', error);
      }
    }
  }, [data, isJsonMode, isDirty]);

  const toggleNode = (nodeId) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const handleValueChange = useCallback((path, rawValue, originalValue) => {
    if (!onChange) return;
    const newValue = coerceValue(rawValue, originalValue);
    const updated = cloneWithUpdatedPath(data, path, newValue);
    onChange(updated);
  }, [data, onChange]);

  const addArrayItem = useCallback((path) => {
    if (!onChange) return;
    
    const targetArray = path.length === 0 ? data : path.reduce((obj, key) => obj[key], data);
    if (!Array.isArray(targetArray)) return;
    
    const emptyItem = createEmptyArrayItem(targetArray);
    const newArray = [...targetArray, emptyItem];
    const updated = cloneWithUpdatedPath(data, path, newArray);
    onChange(updated);
  }, [data, onChange]);

  const deleteArrayItem = useCallback((path, index) => {
    if (!onChange) return;
    
    const targetArray = path.length === 0 ? data : path.reduce((obj, key) => obj[key], data);
    if (!Array.isArray(targetArray)) return;
    
    const newArray = targetArray.filter((_, i) => i !== index);
    const updated = cloneWithUpdatedPath(data, path, newArray);
    onChange(updated);
  }, [data, onChange]);

  // Separate validation function for cleaner code
  const validateJson = useCallback((jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      
      // Only update if the data actually changed
      if (JSON.stringify(parsed) !== JSON.stringify(data)) {
        onChange?.(parsed);
      }
      
      // Clear error state if needed
      if (jsonError) {
        setJsonError(null);
        onJsonError?.('', false);
      }
    } catch (error) {
      // Set error state
      setJsonError('Invalid JSON syntax');
      
      // Animate error only if not already animating
      if (!isErrorAnimating) {
        setIsErrorAnimating(true);
        setTimeout(() => setIsErrorAnimating(false), 500);
      }
      
      // Report error to parent
      onJsonError?.('', true);
    }
  }, [data, onChange, jsonError, onJsonError, isErrorAnimating]);

  // Handle JSON text changes
  const handleJsonChange = useCallback((newValue) => {
    setJsonValue(newValue);
    setIsDirty(true);
    
    // Clear previous timeout to debounce validation
    if (validationTimerRef.current) {
      clearTimeout(validationTimerRef.current);
    }
    
    // Debounce validation to prevent excessive error reporting
    validationTimerRef.current = setTimeout(() => {
      validateJson(newValue);
    }, 50);
  }, [validateJson]);

  // Toggle between JSON and tree view
  const toggleJsonView = useCallback(() => {
    if (isJsonMode) {
      // Validate JSON before switching to tree view
      try {
        JSON.parse(jsonValue);
        setIsDirty(false);
      } catch (error) {
        // Don't allow switching if JSON is invalid
        return;
      }
    } else {
      try {
        setJsonValue(JSON.stringify(data, null, 2));
        setIsDirty(false);
      } catch (error) {
        console.error('Error formatting JSON:', error);
      }
    }
    setIsJsonMode(!isJsonMode);
  }, [isJsonMode, jsonValue, data]);

  const renderValueCell = useCallback((path, value) => {
    const originalType = typeof value;

    if (!editable || isObjectLike(value)) {
      return (
        <Typography
          variant="body2"
          sx={{ color: isObjectLike(value) ? 'text.secondary' : 'text.primary', whiteSpace: 'pre-wrap' }}
        >
          {isObjectLike(value) ? getNodeSummary(value) : formatLeafValue(value)}
        </Typography>
      );
    }

    if (originalType === 'boolean') {
      return (
        <TextField
          select
          size="small"
          value={value ? 'true' : 'false'}
          onChange={(e) => handleValueChange(path, e.target.value, value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="true">true</MenuItem>
          <MenuItem value="false">false</MenuItem>
        </TextField>
      );
    }

    if (originalType === 'number') {
      return (
        <TextField
          type="number"
          size="small"
          value={value ?? ''}
          onChange={(e) => handleValueChange(path, e.target.value, value)}
          sx={{ minWidth: 160 }}
        />
      );
    }

    const stringValue = value === null || value === undefined ? '' : String(value);
    const isMultiline = stringValue.includes('\n') || stringValue.length > 120;

    return (
      <TextField
        fullWidth
        size="small"
        multiline={isMultiline}
        minRows={isMultiline ? 3 : 1}
        value={stringValue}
        onChange={(e) => handleValueChange(path, e.target.value, value)}
      />
    );
  }, [editable, handleValueChange]);

  const renderRows = useMemo(() => {
    const buildRows = (value, path = [], depth = 0) => {
      if (!isObjectLike(value)) {
        return [];
      }

      const isParentArray = Array.isArray(value);
      const entries = isParentArray
        ? value.map((child, index) => [String(index), child])
        : Object.entries(value);

      if (entries.length === 0) {
        const nodeId = createNodeId(path);
        const label = path.length ? path[path.length - 1] : 'Root';
        return [
          <TableRow key={`${nodeId}-empty`}>
            <TableCell sx={{ pl: depth * 2, width: '30%', minWidth: 240 }}>
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
            </TableCell>
            <TableCell sx={{ width: '70%' }}>
              <Typography variant="body2" color="text.disabled">(vuoto)</Typography>
            </TableCell>
          </TableRow>
        ];
      }

      const rows = entries.flatMap(([key, child]) => {
        const nodePath = [...path, key];
        const nodeId = createNodeId(nodePath);
        const childHasNodes = hasChildNodes(child);
        const isExpanded = expandedNodes.has(nodeId);
        const label = isParentArray ? `#${Number(key) + 1}` : key;

        const row = (
          <TableRow key={nodeId}>
            <TableCell sx={{ verticalAlign: 'top', width: '30%', minWidth: 240 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', pl: depth * 2 }}>
                {childHasNodes && (
                  <IconButton size="small" onClick={() => toggleNode(nodeId)} sx={{ mr: 1 }}>
                    {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                  </IconButton>
                )}
                {!childHasNodes && (
                  <Box sx={{ width: 32, mr: 1 }} />
                )}
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', wordBreak: 'break-word' }}>
                  {label}
                </Typography>
                {/* Delete button for array items */}
                {editable && isParentArray && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteArrayItem(path, Number(key));
                    }}
                    sx={{
                      ml: 1,
                      color: 'error.main',
                      '&:hover': { backgroundColor: 'error.light', color: 'white' }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </TableCell>
            <TableCell sx={{ verticalAlign: 'top', width: '70%' }}>
              {renderValueCell(nodePath, child)}
            </TableCell>
          </TableRow>
        );

        const childRows = childHasNodes && isExpanded
          ? buildRows(child, nodePath, depth + 1)
          : [];

        return [row, ...childRows];
      });

      // Add "Add Item" button for arrays when editable
      if (editable && isParentArray) {
        rows.push(
          <TableRow key={`${createNodeId(path)}-add`}>
            <TableCell colSpan={2} sx={{ textAlign: 'center', py: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => addArrayItem(path)}
                sx={{
                  borderStyle: 'dashed',
                  '&:hover': { borderStyle: 'solid' }
                }}
              >
                Add Item
              </Button>
            </TableCell>
          </TableRow>
        );
      }

      return rows;
    };

    if (!isObjectLike(data)) {
      return [
        <TableRow key="root-value">
          <TableCell sx={{ width: '30%', minWidth: 240 }}>
            <Typography variant="body2" color="text.secondary">Root</Typography>
          </TableCell>
          <TableCell sx={{ width: '70%' }}>
            {renderValueCell([], data)}
          </TableCell>
        </TableRow>
      ];
    }

    return buildRows(data);
  }, [data, expandedNodes, renderValueCell, editable, addArrayItem, deleteArrayItem]);

  // Early return if data is invalid
  if (!data || typeof data !== 'object') {
    return (
      <TableContainer component={Paper} variant="outlined" sx={sx}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell colSpan={2}>
                <Typography variant="body2" color="text.disabled">
                  No data to display
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Box sx={sx}>
      {/* JSON Error Alert */}
      {jsonError && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2,
            animation: isErrorAnimating ? 'shake 0.5s' : 'none',
            '@keyframes shake': {
              '0%, 100%': { transform: 'translateX(0)' },
              '25%': { transform: 'translateX(-5px)' },
              '75%': { transform: 'translateX(5px)' }
            }
          }}
        >
          {jsonError}
        </Alert>
      )}

      {/* JSON View Mode */}
      {isJsonMode ? (
        <Box>
          {/* JSON Toggle Button */}
          {showJsonToggle && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CodeIcon />}
                onClick={toggleJsonView}
              >
                Tree View
              </Button>
            </Box>
          )}
          
          {/* JSON TextField */}
          <TextField
            fullWidth
            multiline
            rows={20}
            value={jsonValue}
            onChange={(e) => handleJsonChange(e.target.value)}
            error={!!jsonError}
            disabled={!editable}
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              }
            }}
          />
        </Box>
      ) : (
        <Box>
          {/* Tree View Mode */}
          {showJsonToggle && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CodeIcon />}
                onClick={toggleJsonView}
              >
                JSON View
              </Button>
            </Box>
          )}
          
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableBody>
                {renderRows}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default StructuredDataTree;
